import React, { lazy, Suspense } from 'react';
import { useAuth } from '../components/AuthProvider';
import { useLocaleContext } from '../context/LocaleContext';
import WatermarkRemoverClient from '../components/WatermarkRemover.client';
import { generateFAQSchema, injectFAQSchema } from '../utils/faqSchema';
import { RelatedContentForPSEO } from '../components/RelatedContentForPSEO';
import { Link } from 'react-router-dom';

const FeedbackCTA = lazy(() => import('../components/FeedbackCTA'));
const RelatedTools = lazy(() => import('../components/RelatedTools'));
import GeminiImageBanner from '../components/GeminiImageBanner';
import {
  CheckCircle2,
  Shield,
  Zap,
  FileText,
  GraduationCap,
  ChevronDown,
  ArrowRight,
  HelpCircle,
  Gift,
  Bot,
  Users,
  Copy,
  AlertTriangle,
  Eye,
  Lock,
  Type,
  Binary,
  Eraser,
} from 'lucide-react';

type LocaleKey = 'en' | 'de' | 'pt-BR' | 'ms-MY' | 'es' | 'fr' | 'ja' | 'id';

/**
 * Layout 1: Direct-Action Minimalist
 * Ultra-simple, focused on instant tool usage with tool very high on page.
 * Clean, minimal, fast. No fluff.
 */
export default function FreeAiTextWatermarkRemoverPage() {
  const { user } = useAuth();
  const { locale } = useLocaleContext();
  const [openFAQIndex, setOpenFAQIndex] = React.useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQIndex(openFAQIndex === index ? null : index);
  };

  const t = {
    hero: {
      title: {
        en: 'Free AI Text Watermark Remover, No Signup',
        de: 'Kostenloser KI-Text Wasserzeichen Entferner, Ohne Anmeldung',
        'pt-BR': 'Removedor de Marca d\'Água de Texto IA Grátis, Sem Cadastro',
        'ms-MY': 'Penyingkir Tanda Air Teks AI Percuma, Tanpa Pendaftaran',
        es: 'Eliminador de Marca de Agua de Texto IA Gratis, Sin Registro',
        fr: 'Suppresseur de Filigrane IA Gratuit, Sans Inscription',
        ja: '無料AIテキスト透かし除去ツール、登録不要',
        id: 'Penghapus Watermark Teks AI Gratis, Tanpa Daftar',
      },
      subtitle: {
        en: 'Paste AI text from any model. Clean instantly.',
        de: 'KI-Text von jedem Modell einfügen. Sofort bereinigen.',
        'pt-BR': 'Cole texto de IA de qualquer modelo. Limpe instantaneamente.',
        'ms-MY': 'Tampal teks AI dari mana-mana model. Bersihkan serta-merta.',
        es: 'Pega texto de IA de cualquier modelo. Limpia al instante.',
        fr: 'Collez du texte IA de n\'importe quel modèle. Nettoyage instantané.',
        ja: 'AIモデルのテキストを貼り付けて即座にクリーニング。',
        id: 'Tempel teks AI dari model apapun. Bersihkan seketika.',
      },
      cta: {
        en: 'Start Cleaning →',
        de: 'Bereinigung starten →',
        'pt-BR': 'Começar Limpeza →',
        'ms-MY': 'Mula Bersihkan →',
        es: 'Iniciar Limpieza →',
        fr: 'Commencer le Nettoyage →',
        ja: 'クリーニング開始 →',
        id: 'Mulai Bersihkan →',
      },
    },
    toolInstruction: {
      en: 'Paste text → Clean instantly',
      de: 'Text einfügen → Sofort bereinigen',
      'pt-BR': 'Cole o texto → Limpe instantaneamente',
      'ms-MY': 'Tampal teks → Bersihkan serta-merta',
      es: 'Pega texto → Limpia al instante',
      fr: 'Collez le texte → Nettoyage immédiat',
      ja: 'テキストを貼り付け → 即座にクリーン',
      id: 'Tempel teks → Langsung bersih',
    },
    freeTierHighlights: {
      title: {
        en: 'Free Forever',
        de: 'Für immer kostenlos',
        'pt-BR': 'Grátis Para Sempre',
        'ms-MY': 'Percuma Selamanya',
        es: 'Gratis Para Siempre',
        fr: 'Gratuit Pour Toujours',
        ja: '永久無料',
        id: 'Gratis Selamanya',
      },
      items: {
        en: [
          '500 characters free per paste',
          'Unlimited uses, no daily limits',
          'No login or signup required',
          'Works with text pasted from any AI tool',
          '100% browser-based, your text stays private',
        ],
        de: [
          '500 Zeichen kostenlos pro Einfügen',
          'Unbegrenzte Nutzung, keine täglichen Limits',
          'Keine Anmeldung erforderlich',
          'Funktioniert mit Text aus jedem KI-Tool',
          '100% browserbasiert, Ihr Text bleibt privat',
        ],
        'pt-BR': [
          '500 caracteres grátis por colagem',
          'Usos ilimitados, sem limites diários',
          'Sem login ou cadastro necessário',
          'Funciona com texto de qualquer ferramenta de IA',
          '100% baseado no navegador, seu texto permanece privado',
        ],
        'ms-MY': [
          '500 aksara percuma setiap tampal',
          'Penggunaan tanpa had, tiada had harian',
          'Tiada log masuk atau pendaftaran diperlukan',
          'Berfungsi dengan teks dari mana-mana alat AI',
          '100% berasaskan pelayar, teks anda kekal peribadi',
        ],
        es: [
          '500 caracteres gratis por pegado',
          'Usos ilimitados, sin límites diarios',
          'Sin login ni registro necesario',
          'Funciona con texto de cualquier herramienta de IA',
          '100% basado en navegador, tu texto permanece privado',
        ],
        fr: [
          '500 caractères gratuits par collage',
          'Utilisations illimitées, aucune limite quotidienne',
          'Aucune connexion ni inscription requise',
          'Compatible avec du texte provenant de tout outil IA',
          '100% dans le navigateur, votre texte reste privé',
        ],
        ja: [
          '1回の貼り付けにつき500文字無料',
          '無制限の使用回数、1日の制限なし',
          'ログインや登録は不要',
          'あらゆるAIツールからのテキストに対応',
          '100%ブラウザベース、テキストは完全プライベート',
        ],
        id: [
          '500 karakter gratis per tempel',
          'Penggunaan tak terbatas, tanpa batas harian',
          'Tidak perlu login atau daftar',
          'Bekerja dengan teks dari alat AI mana pun',
          '100% berbasis browser, teks Anda tetap pribadi',
        ],
      },
    },
    proofSection: {
      title: {
        en: 'Trusted by our users',
        de: 'Von unseren Nutzern vertraut',
        'pt-BR': 'Confiado por nossos usuários',
        'ms-MY': 'Dipercayai oleh pengguna kami',
        es: 'Confiado por nuestros usuarios',
        fr: 'La confiance de nos utilisateurs',
        ja: 'ユーザーに信頼されています',
        id: 'Dipercaya oleh pengguna kami',
      },
      stats: {
        en: [
          { value: '0', label: 'Data Stored' },
          { value: '100%', label: 'Browser-Based' },
        ],
        de: [
          { value: '0', label: 'Daten gespeichert' },
          { value: '100%', label: 'Browserbasiert' },
        ],
        'pt-BR': [
          { value: '0', label: 'Dados Armazenados' },
          { value: '100%', label: 'Baseado no Navegador' },
        ],
        'ms-MY': [
          { value: '0', label: 'Data Disimpan' },
          { value: '100%', label: 'Berasaskan Pelayar' },
        ],
        es: [
          { value: '0', label: 'Datos Almacenados' },
          { value: '100%', label: 'Basado en Navegador' },
        ],
        fr: [
          { value: '0', label: 'Données Stockées' },
          { value: '100%', label: 'Dans le Navigateur' },
        ],
        ja: [
          { value: '0', label: 'データ保存なし' },
          { value: '100%', label: 'ブラウザベース' },
        ],
        id: [
          { value: '0', label: 'Data Tersimpan' },
          { value: '100%', label: 'Berbasis Browser' },
        ],
      },
    },
    faqTitle: {
      en: 'Quick Answers',
      de: 'Schnelle Antworten',
      'pt-BR': 'Respostas Rápidas',
      'ms-MY': 'Jawapan Pantas',
      es: 'Respuestas Rápidas',
      fr: 'Réponses Rapides',
      ja: 'よくある質問',
      id: 'Jawaban Cepat',
    },
    relatedTutorials: {
      title: {
        en: 'Learn More',
        de: 'Mehr erfahren',
        'pt-BR': 'Saiba Mais',
        'ms-MY': 'Ketahui Lebih Lanjut',
        es: 'Aprende Más',
        fr: 'En Savoir Plus',
        ja: 'もっと詳しく',
        id: 'Pelajari Selengkapnya',
      },
    },
    stepByStep: {
      title: {
        en: 'How to Clean AI Text in 3 Simple Steps',
        de: 'So bereinigen Sie KI-Text in 3 einfachen Schritten',
        'pt-BR': 'Como Limpar Texto de IA em 3 Passos Simples',
        'ms-MY': 'Cara Membersihkan Teks AI dalam 3 Langkah Mudah',
        es: 'Cómo Limpiar Texto de IA en 3 Pasos Simples',
        fr: 'Comment Nettoyer un Texte IA en 3 Étapes',
        ja: '3ステップでAIテキストをクリーン',
        id: 'Cara Bersihkan Teks AI dalam 3 Langkah',
      },
      steps: {
        en: [
          { number: '1', title: 'Copy Your AI Text', description: 'Copy text from any AI tool. Invisible characters can come along without you noticing.' },
          { number: '2', title: 'Paste & Click Clean', description: 'Paste your text into the tool above and click the clean button. Processing happens instantly in your browser.' },
          { number: '3', title: 'Copy Clean Text', description: 'Your text is now free of invisible watermarks. Copy and use it anywhere — documents, emails, posts.' },
        ],
        de: [
          { number: '1', title: 'KI-Text kopieren', description: 'Kopieren Sie Text aus einem beliebigen KI-Tool. Unsichtbare Zeichen können unbemerkt mitkommen.' },
          { number: '2', title: 'Einfügen & Bereinigen', description: 'Fügen Sie Ihren Text in das Tool ein und klicken Sie auf Bereinigen. Die Verarbeitung erfolgt sofort in Ihrem Browser.' },
          { number: '3', title: 'Sauberen Text kopieren', description: 'Ihr Text ist jetzt frei von unsichtbaren Wasserzeichen. Kopieren und überall verwenden.' },
        ],
        'pt-BR': [
          { number: '1', title: 'Copie Seu Texto de IA', description: 'Copie texto de qualquer ferramenta de IA. Caracteres invisíveis podem vir junto sem você perceber.' },
          { number: '2', title: 'Cole e Limpe', description: 'Cole seu texto na ferramenta acima e clique no botão limpar. O processamento acontece instantaneamente no seu navegador.' },
          { number: '3', title: 'Copie o Texto Limpo', description: 'Seu texto agora está livre de marcas d\'água invisíveis. Copie e use em qualquer lugar.' },
        ],
        'ms-MY': [
          { number: '1', title: 'Salin Teks AI Anda', description: 'Salin teks dari mana-mana alat AI. Aksara tidak kelihatan mungkin ikut sama tanpa anda sedari.' },
          { number: '2', title: 'Tampal & Bersihkan', description: 'Tampal teks anda ke dalam alat di atas dan klik butang bersih. Pemprosesan berlaku serta-merta dalam pelayar anda.' },
          { number: '3', title: 'Salin Teks Bersih', description: 'Teks anda kini bebas daripada tanda air tidak kelihatan. Salin dan gunakan di mana-mana.' },
        ],
        es: [
          { number: '1', title: 'Copia Tu Texto de IA', description: 'Copia texto de cualquier herramienta de IA. Los caracteres invisibles pueden venir sin que lo notes.' },
          { number: '2', title: 'Pega y Limpia', description: 'Pega tu texto en la herramienta de arriba y haz clic en limpiar. El procesamiento ocurre instantáneamente en tu navegador.' },
          { number: '3', title: 'Copia el Texto Limpio', description: 'Tu texto ahora está libre de marcas de agua invisibles. Copia y úsalo donde quieras.' },
        ],
        fr: [
          { number: '1', title: 'Copiez Votre Texte IA', description: 'Copiez le texte depuis n\'importe quel outil IA. Des caractères invisibles peuvent s\'y glisser sans que vous le remarquiez.' },
          { number: '2', title: 'Collez et Nettoyez', description: 'Collez votre texte dans l\'outil ci-dessus et cliquez sur nettoyer. Le traitement est instantané dans votre navigateur.' },
          { number: '3', title: 'Copiez le Texte Propre', description: 'Votre texte est maintenant exempt de filigranes invisibles. Copiez et utilisez-le partout.' },
        ],
        ja: [
          { number: '1', title: 'AIテキストをコピー', description: 'どのAIツールからでもテキストをコピーできます。気づかないうちに不可視文字が一緒に含まれることがあります。' },
          { number: '2', title: '貼り付けてクリーニング', description: '上のツールにテキストを貼り付けてクリーンボタンをクリック。処理はブラウザ内で即座に完了。' },
          { number: '3', title: 'クリーンなテキストをコピー', description: 'テキストから見えない透かしが除去されました。どこでもコピーして使用できます。' },
        ],
        id: [
          { number: '1', title: 'Salin Teks AI Anda', description: 'Salin teks dari alat AI mana pun. Karakter tak terlihat bisa ikut tersalin tanpa Anda sadari.' },
          { number: '2', title: 'Tempel & Bersihkan', description: 'Tempel teks Anda ke alat di atas dan klik tombol bersihkan. Pemrosesan terjadi instan di browser.' },
          { number: '3', title: 'Salin Teks Bersih', description: 'Teks Anda sekarang bebas dari watermark tersembunyi. Salin dan gunakan di mana saja.' },
        ],
      },
    },
    invisibleChars: {
      title: {
        en: 'What Invisible Characters Are Hidden in Your AI Text?',
        de: 'Welche unsichtbaren Zeichen verstecken sich in Ihrem KI-Text?',
        'pt-BR': 'Que Caracteres Invisíveis Estão Escondidos no Seu Texto de IA?',
        'ms-MY': 'Apakah Aksara Tidak Kelihatan yang Tersembunyi dalam Teks AI Anda?',
        es: '¿Qué Caracteres Invisibles Están Ocultos en Tu Texto de IA?',
        fr: 'Quels Caractères Invisibles Se Cachent dans Votre Texte IA ?',
        ja: 'AIテキストに隠された見えない文字とは？',
        id: 'Karakter Tak Terlihat Apa yang Tersembunyi di Teks AI Anda?',
      },
      tableHeaders: {
        en: { code: 'Code', name: 'Name', purpose: 'Purpose' },
        de: { code: 'Code', name: 'Name', purpose: 'Zweck' },
        'pt-BR': { code: 'Código', name: 'Nome', purpose: 'Finalidade' },
        'ms-MY': { code: 'Kod', name: 'Nama', purpose: 'Tujuan' },
        es: { code: 'Código', name: 'Nombre', purpose: 'Finalidad' },
        fr: { code: 'Code', name: 'Nom', purpose: 'Objet' },
        ja: { code: 'コード', name: '名前', purpose: '内容' },
        id: { code: 'Kode', name: 'Nama', purpose: 'Fungsi' },
      },
      description: {
        en: 'These invisible Unicode characters turn up in text copied out of AI tools:',
        de: 'Diese unsichtbaren Unicode-Zeichen tauchen in Text auf, der aus KI-Tools kopiert wird:',
        'pt-BR': 'Esses caracteres Unicode invisíveis aparecem em texto copiado de ferramentas de IA:',
        'ms-MY': 'Aksara Unicode tidak kelihatan ini muncul dalam teks yang disalin dari alat AI:',
        es: 'Estos caracteres Unicode invisibles aparecen en texto copiado de herramientas de IA:',
        fr: 'Ces caractères Unicode invisibles apparaissent dans le texte copié depuis des outils IA :',
        ja: 'これらの不可視Unicode文字は、AIツールからコピーしたテキストに現れます。',
        id: 'Karakter Unicode tak terlihat ini muncul dalam teks yang disalin dari alat AI:',
      },
      chars: {
        en: [
          { code: 'U+200B', name: 'Zero-Width Space', description: 'A space with no width. Inflates character counts and breaks search and find-and-replace.' },
          { code: 'U+200C', name: 'Zero-Width Non-Joiner', description: 'Stops adjacent characters from visually joining. Corrupts string comparisons and exact-match search.' },
          { code: 'U+200D', name: 'Zero-Width Joiner', description: 'Forces adjacent characters to visually join. Can break text rendering and string matching.' },
          { code: 'U+2060', name: 'Word Joiner', description: 'Blocks a line break at that point. Produces odd wrapping and mismatched text comparisons.' },
          { code: 'U+FEFF', name: 'Byte Order Mark', description: 'A file-encoding marker meant only for the start of a file. Mid-text, it breaks JSON, CSV, and code parsers.' },
          { code: 'U+00AD', name: 'Soft Hyphen', description: 'An optional hyphenation point, invisible unless a line wraps there. Can silently split words during search or copy-paste.' },
        ],
        de: [
          { code: 'U+200B', name: 'Zero-Width Space', description: 'Ein Leerzeichen ohne Breite. Verfälscht Zeichenzählungen und stört Suche sowie Suchen-und-Ersetzen.' },
          { code: 'U+200C', name: 'Zero-Width Non-Joiner', description: 'Verhindert die optische Verbindung benachbarter Zeichen. Verfälscht Textvergleiche und exakte Suche.' },
          { code: 'U+200D', name: 'Zero-Width Joiner', description: 'Erzwingt die optische Verbindung benachbarter Zeichen. Kann Textdarstellung und Textabgleich stören.' },
          { code: 'U+2060', name: 'Word Joiner', description: 'Blockiert an dieser Stelle einen Zeilenumbruch. Erzeugt seltsame Umbrüche und fehlerhafte Textvergleiche.' },
          { code: 'U+FEFF', name: 'Byte Order Mark', description: 'Ein Kodierungsmarker, der nur an den Dateianfang gehört. Mitten im Text stört er JSON-, CSV- und Code-Parser.' },
          { code: 'U+00AD', name: 'Soft Hyphen', description: 'Ein optionaler Trennungspunkt, unsichtbar außer bei Zeilenumbruch. Kann Wörter bei Suche oder Kopieren unbemerkt trennen.' },
        ],
        'pt-BR': [
          { code: 'U+200B', name: 'Espaço de Largura Zero', description: 'Um espaço sem largura. Infla a contagem de caracteres e quebra a busca e o localizar-e-substituir.' },
          { code: 'U+200C', name: 'Não-Junção de Largura Zero', description: 'Impede que caracteres adjacentes se unam visualmente. Corrompe comparações de texto e buscas exatas.' },
          { code: 'U+200D', name: 'Junção de Largura Zero', description: 'Força a união visual de caracteres adjacentes. Pode quebrar a renderização e a correspondência de texto.' },
          { code: 'U+2060', name: 'Juntor de Palavras', description: 'Bloqueia uma quebra de linha naquele ponto. Produz quebras estranhas e comparações de texto incorretas.' },
          { code: 'U+FEFF', name: 'Marca de Ordem de Byte', description: 'Um marcador de codificação destinado apenas ao início de um arquivo. No meio do texto, quebra parsers de JSON, CSV e código.' },
          { code: 'U+00AD', name: 'Hífen Suave', description: 'Um ponto de hifenização opcional, invisível a menos que a linha quebre ali. Pode separar palavras silenciosamente durante busca ou colagem.' },
        ],
        'ms-MY': [
          { code: 'U+200B', name: 'Ruang Lebar Sifar', description: 'Ruang tanpa lebar. Menggelembungkan kiraan aksara dan merosakkan carian serta cari-dan-ganti.' },
          { code: 'U+200C', name: 'Bukan Penyambung Lebar Sifar', description: 'Menghalang aksara bersebelahan daripada bercantum secara visual. Merosakkan perbandingan teks dan carian tepat.' },
          { code: 'U+200D', name: 'Penyambung Lebar Sifar', description: 'Memaksa aksara bersebelahan bercantum secara visual. Boleh merosakkan paparan teks dan padanan rentetan.' },
          { code: 'U+2060', name: 'Penyambung Perkataan', description: 'Menyekat pemisahan baris pada titik itu. Menghasilkan pembalutan pelik dan perbandingan teks yang tidak sepadan.' },
          { code: 'U+FEFF', name: 'Tanda Susunan Bait', description: 'Penanda pengekodan yang sepatutnya hanya di permulaan fail. Di tengah teks, ia merosakkan penghurai JSON, CSV dan kod.' },
          { code: 'U+00AD', name: 'Tanda Sempang Lembut', description: 'Titik tanda sempang pilihan, tidak kelihatan melainkan baris membalut di situ. Boleh memisahkan perkataan secara senyap semasa carian atau salin-tampal.' },
        ],
        es: [
          { code: 'U+200B', name: 'Espacio de Ancho Cero', description: 'Un espacio sin ancho. Infla el recuento de caracteres y rompe la búsqueda y el buscar-y-reemplazar.' },
          { code: 'U+200C', name: 'No-Unión de Ancho Cero', description: 'Evita que caracteres adyacentes se unan visualmente. Corrompe comparaciones de texto y búsquedas exactas.' },
          { code: 'U+200D', name: 'Unión de Ancho Cero', description: 'Fuerza la unión visual de caracteres adyacentes. Puede romper la representación de texto y la coincidencia de cadenas.' },
          { code: 'U+2060', name: 'Unidor de Palabras', description: 'Bloquea un salto de línea en ese punto. Produce ajustes de línea extraños y comparaciones de texto incorrectas.' },
          { code: 'U+FEFF', name: 'Marca de Orden de Byte', description: 'Un marcador de codificación pensado solo para el inicio de un archivo. En medio del texto, rompe analizadores de JSON, CSV y código.' },
          { code: 'U+00AD', name: 'Guión Suave', description: 'Un punto de separación silábica opcional, invisible salvo que la línea se corte ahí. Puede dividir palabras silenciosamente al buscar o pegar.' },
        ],
        fr: [
          { code: 'U+200B', name: 'Espace sans chasse', description: 'Un espace sans largeur. Gonfle le décompte de caractères et casse la recherche et le rechercher-remplacer.' },
          { code: 'U+200C', name: 'Antiliant sans chasse', description: 'Empêche des caractères adjacents de se lier visuellement. Corrompt les comparaisons de texte et la recherche exacte.' },
          { code: 'U+200D', name: 'Liant sans chasse', description: 'Force la liaison visuelle de caractères adjacents. Peut casser le rendu du texte et la correspondance de chaînes.' },
          { code: 'U+2060', name: 'Inséparables', description: 'Bloque un saut de ligne à cet endroit. Produit des retours à la ligne étranges et des comparaisons de texte erronées.' },
          { code: 'U+FEFF', name: 'Indicateur d\'ordre des octets', description: 'Un marqueur d\'encodage destiné uniquement au début d\'un fichier. Au milieu du texte, il casse les analyseurs JSON, CSV et de code.' },
          { code: 'U+00AD', name: 'Trait d\'union conditionnel', description: 'Un point de césure optionnel, invisible sauf si la ligne se coupe à cet endroit. Peut scinder des mots silencieusement lors d\'une recherche ou d\'un copier-coller.' },
        ],
        ja: [
          { code: 'U+200B', name: 'ゼロ幅スペース', description: '幅を持たないスペース。文字数を水増しし、検索や検索・置換を壊します。' },
          { code: 'U+200C', name: 'ゼロ幅非接合子', description: '隣接する文字の視覚的な結合を止めます。文字列比較や完全一致検索を狂わせます。' },
          { code: 'U+200D', name: 'ゼロ幅接合子', description: '隣接する文字を視覚的に結合させます。テキスト表示や文字列照合を壊すことがあります。' },
          { code: 'U+2060', name: 'ワードジョイナー', description: 'その位置での改行を防ぎます。不自然な折り返しやテキスト比較の不一致を生みます。' },
          { code: 'U+FEFF', name: 'バイト順マーク', description: '本来はファイル冒頭専用のエンコーディングマーカー。文中にあるとJSON、CSV、コードのパーサーを壊します。' },
          { code: 'U+00AD', name: 'ソフトハイフン', description: 'オプションのハイフネーションポイントで、行が折り返す位置以外では不可視。検索やコピー時に単語を気づかぬうちに分断することがあります。' },
        ],
        id: [
          { code: 'U+200B', name: 'Spasi Tanpa Lebar', description: 'Spasi tanpa lebar. Menggelembungkan hitungan karakter dan merusak pencarian serta cari-dan-ganti.' },
          { code: 'U+200C', name: 'Non-Joiner Tanpa Lebar', description: 'Mencegah karakter yang berdekatan bergabung secara visual. Merusak perbandingan teks dan pencarian tepat.' },
          { code: 'U+200D', name: 'Joiner Tanpa Lebar', description: 'Memaksa karakter yang berdekatan bergabung secara visual. Dapat merusak tampilan teks dan pencocokan string.' },
          { code: 'U+2060', name: 'Word Joiner', description: 'Memblokir pemisahan baris di titik itu. Menghasilkan pembungkusan aneh dan perbandingan teks yang tidak cocok.' },
          { code: 'U+FEFF', name: 'Byte Order Mark', description: 'Penanda encoding yang seharusnya hanya di awal file. Di tengah teks, ini merusak parser JSON, CSV, dan kode.' },
          { code: 'U+00AD', name: 'Soft Hyphen', description: 'Titik pemenggalan opsional, tak terlihat kecuali baris terpotong di situ. Dapat memisahkan kata secara diam-diam saat pencarian atau salin-tempel.' },
        ],
      },
    },
    textProblems: {
      title: {
        en: 'Common Text Problems Caused by AI Watermarks',
        de: 'Häufige Textprobleme durch KI-Wasserzeichen',
        'pt-BR': 'Problemas Comuns de Texto Causados por Marcas d\'Água de IA',
        'ms-MY': 'Masalah Teks Biasa yang Disebabkan oleh Tanda Air AI',
        es: 'Problemas Comunes de Texto Causados por Marcas de Agua de IA',
        fr: 'Problèmes de Texte Courants Causés par les Filigranes IA',
        ja: 'AI透かしが引き起こす一般的なテキスト問題',
        id: 'Masalah Teks Umum yang Disebabkan Watermark AI',
      },
      problems: {
        en: [
          { icon: Type, title: 'Broken Search & Find-Replace', description: 'Invisible characters break search, find-and-replace, and string matching, so text that looks identical on screen fails to match.' },
          { icon: FileText, title: 'Document Formatting Issues', description: 'Invisible characters can break formatting when pasting into Word, Google Docs, or design software.' },
          { icon: Binary, title: 'Code & Data Corruption', description: 'Zero-width characters in code comments or data fields can cause bugs, parsing errors, or database issues.' },
          { icon: Copy, title: 'Copy-Paste Artifacts', description: 'Text may behave unexpectedly when copied multiple times, with hidden characters accumulating.' },
        ],
        de: [
          { icon: Type, title: 'Defekte Suche & Suchen-und-Ersetzen', description: 'Unsichtbare Zeichen stören Suche, Suchen-und-Ersetzen und Textabgleich, sodass optisch identischer Text nicht übereinstimmt.' },
          { icon: FileText, title: 'Dokumentformatierungsprobleme', description: 'Unsichtbare Zeichen können die Formatierung beim Einfügen in Word oder Google Docs stören.' },
          { icon: Binary, title: 'Code- und Datenbeschädigung', description: 'Zero-Width-Zeichen in Code können Bugs oder Parsing-Fehler verursachen.' },
          { icon: Copy, title: 'Kopier-Artefakte', description: 'Text kann sich unerwartet verhalten, wenn er mehrfach kopiert wird.' },
        ],
        'pt-BR': [
          { icon: Type, title: 'Busca e Localizar-Substituir Quebrados', description: 'Caracteres invisíveis quebram a busca, o localizar-e-substituir e a correspondência de texto, fazendo com que texto visualmente idêntico não corresponda.' },
          { icon: FileText, title: 'Problemas de Formatação', description: 'Caracteres invisíveis podem quebrar a formatação ao colar no Word ou Google Docs.' },
          { icon: Binary, title: 'Corrupção de Código e Dados', description: 'Caracteres de largura zero em código podem causar bugs ou erros de análise.' },
          { icon: Copy, title: 'Artefatos de Cópia', description: 'Texto pode se comportar inesperadamente quando copiado múltiplas vezes.' },
        ],
        'ms-MY': [
          { icon: Type, title: 'Carian & Cari-Ganti Rosak', description: 'Aksara tidak kelihatan merosakkan carian, cari-dan-ganti, serta padanan rentetan, jadi teks yang kelihatan sama pada skrin gagal dipadankan.' },
          { icon: FileText, title: 'Isu Pemformatan Dokumen', description: 'Aksara tidak kelihatan boleh merosakkan pemformatan apabila menampal ke Word atau Google Docs.' },
          { icon: Binary, title: 'Kerosakan Kod dan Data', description: 'Aksara lebar sifar dalam kod boleh menyebabkan pepijat atau ralat penghuraian.' },
          { icon: Copy, title: 'Artifak Salin-Tampal', description: 'Teks mungkin berkelakuan tidak dijangka apabila disalin berkali-kali.' },
        ],
        es: [
          { icon: Type, title: 'Búsqueda y Buscar-Reemplazar Rotos', description: 'Los caracteres invisibles rompen la búsqueda, el buscar-y-reemplazar y la coincidencia de texto, por lo que texto visualmente idéntico no coincide.' },
          { icon: FileText, title: 'Problemas de Formato', description: 'Los caracteres invisibles pueden romper el formato al pegar en Word o Google Docs.' },
          { icon: Binary, title: 'Corrupción de Código y Datos', description: 'Los caracteres de ancho cero en código pueden causar bugs o errores de análisis.' },
          { icon: Copy, title: 'Artefactos de Copia', description: 'El texto puede comportarse inesperadamente cuando se copia múltiples veces.' },
        ],
        fr: [
          { icon: Type, title: 'Recherche et Rechercher-Remplacer Cassés', description: 'Les caractères invisibles cassent la recherche, le rechercher-remplacer et la correspondance de texte, si bien qu\'un texte visuellement identique ne correspond pas.' },
          { icon: FileText, title: 'Problèmes de Mise en Forme', description: 'Les caractères invisibles peuvent corrompre la mise en forme lors du collage dans Word ou Google Docs.' },
          { icon: Binary, title: 'Corruption de Code et Données', description: 'Les caractères sans chasse dans le code peuvent provoquer des bugs ou des erreurs d\'analyse.' },
          { icon: Copy, title: 'Artéfacts de Copier-Coller', description: 'Le texte peut se comporter de façon inattendue lorsqu\'il est copié plusieurs fois.' },
        ],
        ja: [
          { icon: Type, title: '検索・置換の不具合', description: '不可視文字は検索、検索・置換、文字列照合を壊すため、画面上は同じに見えるテキストが一致しなくなります。' },
          { icon: FileText, title: 'ドキュメント書式の問題', description: '不可視文字はWordやGoogle Docsに貼り付ける際に書式を崩す可能性があります。' },
          { icon: Binary, title: 'コードとデータの破損', description: 'コード内のゼロ幅文字はバグやパースエラーを引き起こす可能性があります。' },
          { icon: Copy, title: 'コピー&ペーストの不具合', description: '複数回コピーすると隠し文字が蓄積され、テキストが予期しない動作をする場合があります。' },
        ],
        id: [
          { icon: Type, title: 'Pencarian & Cari-Ganti Rusak', description: 'Karakter tak terlihat merusak pencarian, cari-dan-ganti, dan pencocokan string, sehingga teks yang terlihat identik di layar gagal cocok.' },
          { icon: FileText, title: 'Masalah Format Dokumen', description: 'Karakter tak terlihat dapat merusak format saat menempel ke Word atau Google Docs.' },
          { icon: Binary, title: 'Kerusakan Kode dan Data', description: 'Karakter tanpa lebar dalam kode dapat menyebabkan bug atau error parsing.' },
          { icon: Copy, title: 'Artefak Salin-Tempel', description: 'Teks mungkin berperilaku tidak terduga saat disalin berkali-kali dengan karakter tersembunyi menumpuk.' },
        ],
      },
    },
    whyClean: {
      title: {
        en: 'Why Clean Your AI-Generated Text?',
        de: 'Warum sollten Sie Ihren KI-generierten Text bereinigen?',
        'pt-BR': 'Por Que Limpar Seu Texto Gerado por IA?',
        'ms-MY': 'Mengapa Bersihkan Teks yang Dijana AI Anda?',
        es: '¿Por Qué Limpiar Tu Texto Generado por IA?',
        fr: 'Pourquoi Nettoyer Votre Texte Généré par IA ?',
        ja: 'AI生成テキストをクリーニングする理由',
        id: 'Mengapa Bersihkan Teks Buatan AI Anda?',
      },
      reasons: {
        en: [
          { icon: Eraser, title: 'Remove Technical Fingerprints', description: 'AI watermarks are technical markers embedded in text. Removing them gives you clean, neutral text without hidden data.' },
          { icon: FileText, title: 'Better Document Compatibility', description: 'Clean text pastes perfectly into any application — Word, Pages, Google Docs, email clients, CMS platforms, and more.' },
          { icon: Eye, title: 'Professional Output', description: 'Ensure your documents, emails, and content don\'t carry invisible artifacts that could cause unexpected issues.' },
          { icon: Lock, title: 'Privacy Control', description: 'Strip out hidden characters so what you share is exactly what you see, with nothing extra travelling along in the text.' },
        ],
        de: [
          { icon: Eraser, title: 'Technische Fingerabdrücke entfernen', description: 'KI-Wasserzeichen sind technische Marker. Entfernen gibt Ihnen sauberen, neutralen Text.' },
          { icon: FileText, title: 'Bessere Dokumentkompatibilität', description: 'Sauberer Text fügt sich perfekt in jede Anwendung ein — Word, Pages, Google Docs, E-Mail.' },
          { icon: Eye, title: 'Professionelle Ausgabe', description: 'Stellen Sie sicher, dass Ihre Dokumente keine unsichtbaren Artefakte enthalten.' },
          { icon: Lock, title: 'Privatsphärekontrolle', description: 'Entfernen Sie versteckte Zeichen, damit das, was Sie teilen, genau dem entspricht, was Sie sehen, ohne dass etwas Zusätzliches im Text mitreist.' },
        ],
        'pt-BR': [
          { icon: Eraser, title: 'Remover Impressões Digitais Técnicas', description: 'Marcas d\'água de IA são marcadores técnicos. Removê-los dá texto limpo e neutro.' },
          { icon: FileText, title: 'Melhor Compatibilidade de Documentos', description: 'Texto limpo cola perfeitamente em qualquer aplicação — Word, Pages, Google Docs, email.' },
          { icon: Eye, title: 'Saída Profissional', description: 'Garanta que seus documentos não carreguem artefatos invisíveis que poderiam causar problemas.' },
          { icon: Lock, title: 'Controle de Privacidade', description: 'Remova caracteres ocultos para que o que você compartilha seja exatamente o que você vê, sem nada extra viajando junto no texto.' },
        ],
        'ms-MY': [
          { icon: Eraser, title: 'Buang Cap Jari Teknikal', description: 'Tanda air AI adalah penanda teknikal. Membuangnya memberi anda teks bersih dan neutral.' },
          { icon: FileText, title: 'Keserasian Dokumen Lebih Baik', description: 'Teks bersih ditampal dengan sempurna ke dalam mana-mana aplikasi — Word, Pages, Google Docs, emel.' },
          { icon: Eye, title: 'Output Profesional', description: 'Pastikan dokumen anda tidak membawa artifak tidak kelihatan yang boleh menyebabkan masalah.' },
          { icon: Lock, title: 'Kawalan Privasi', description: 'Buang aksara tersembunyi supaya apa yang anda kongsi adalah tepat seperti apa yang anda lihat, tanpa apa-apa tambahan yang ikut sama dalam teks.' },
        ],
        es: [
          { icon: Eraser, title: 'Eliminar Huellas Técnicas', description: 'Las marcas de agua de IA son marcadores técnicos. Eliminarlos te da texto limpio y neutral.' },
          { icon: FileText, title: 'Mejor Compatibilidad de Documentos', description: 'El texto limpio se pega perfectamente en cualquier aplicación — Word, Pages, Google Docs, email.' },
          { icon: Eye, title: 'Salida Profesional', description: 'Asegura que tus documentos no lleven artefactos invisibles que podrían causar problemas.' },
          { icon: Lock, title: 'Control de Privacidad', description: 'Elimina caracteres ocultos para que lo que compartes sea exactamente lo que ves, sin nada extra viajando junto en el texto.' },
        ],
        fr: [
          { icon: Eraser, title: 'Supprimer les Empreintes Techniques', description: 'Les filigranes IA sont des marqueurs techniques. Les supprimer vous donne un texte propre et neutre.' },
          { icon: FileText, title: 'Meilleure Compatibilité Documentaire', description: 'Le texte propre se colle parfaitement dans toute application — Word, Pages, Google Docs, email.' },
          { icon: Eye, title: 'Résultat Professionnel', description: 'Assurez-vous que vos documents ne contiennent pas d\'artéfacts invisibles pouvant causer des problèmes.' },
          { icon: Lock, title: 'Contrôle de la Confidentialité', description: 'Supprimez les caractères cachés pour que ce que vous partagez soit exactement ce que vous voyez, sans rien de superflu qui voyage avec le texte.' },
        ],
        ja: [
          { icon: Eraser, title: '技術的フィンガープリントを除去', description: 'AI透かしは技術的なマーカーです。除去することで隠しデータのないクリーンなテキストが得られます。' },
          { icon: FileText, title: 'ドキュメント互換性の向上', description: 'クリーンなテキストはWord、Pages、Google Docs、メールなど、あらゆるアプリケーションに完璧に貼り付けられます。' },
          { icon: Eye, title: 'プロフェッショナルな出力', description: 'ドキュメントやメールに予期しない問題を引き起こす不可視の成果物が含まれないようにします。' },
          { icon: Lock, title: 'プライバシー管理', description: '隠れた文字を除去し、共有する内容が見た目そのままになるようにします。余分なものがテキストに紛れ込むことはありません。' },
        ],
        id: [
          { icon: Eraser, title: 'Hapus Sidik Jari Teknis', description: 'Watermark AI adalah penanda teknis. Menghapusnya memberi Anda teks bersih dan netral tanpa data tersembunyi.' },
          { icon: FileText, title: 'Kompatibilitas Dokumen Lebih Baik', description: 'Teks bersih bisa ditempel dengan sempurna ke aplikasi apapun — Word, Pages, Google Docs, email.' },
          { icon: Eye, title: 'Output Profesional', description: 'Pastikan dokumen Anda tidak membawa artefak tak terlihat yang bisa menyebabkan masalah.' },
          { icon: Lock, title: 'Kontrol Privasi', description: 'Hapus karakter tersembunyi agar apa yang Anda bagikan persis sama dengan apa yang Anda lihat, tanpa ada yang ikut tersembunyi dalam teks.' },
        ],
      },
    },
    comparison: {
      title: {
        en: 'Free vs. Premium: Which Do You Need?',
        de: 'Kostenlos vs. Premium: Was brauchen Sie?',
        'pt-BR': 'Grátis vs. Premium: Qual Você Precisa?',
        'ms-MY': 'Percuma vs. Premium: Yang Mana Anda Perlukan?',
        es: 'Gratis vs. Premium: ¿Cuál Necesitas?',
        fr: 'Gratuit vs. Premium : Lequel Vous Faut-il ?',
        ja: '無料版 vs. プレミアム：どちらが必要？',
        id: 'Gratis vs. Premium: Mana yang Anda Butuhkan?',
      },
      featureLabel: {
        en: 'Feature',
        de: 'Funktion',
        'pt-BR': 'Recurso',
        'ms-MY': 'Ciri',
        es: 'Función',
        fr: 'Fonctionnalité',
        ja: '機能',
        id: 'Fitur',
      },
      premiumCta: {
        en: 'View Premium Plans',
        de: 'Premium-Pläne ansehen',
        'pt-BR': 'Ver Planos Premium',
        'ms-MY': 'Lihat Pelan Premium',
        es: 'Ver Planes Premium',
        fr: 'Voir les Offres Premium',
        ja: 'プレミアムプランを見る',
        id: 'Lihat Paket Premium',
      },
      free: {
        en: 'Free Plan',
        de: 'Kostenloser Plan',
        'pt-BR': 'Plano Grátis',
        'ms-MY': 'Pelan Percuma',
        es: 'Plan Gratis',
        fr: 'Plan Gratuit',
        ja: '無料プラン',
        id: 'Paket Gratis',
      },
      premium: {
        en: 'Premium',
        de: 'Premium',
        'pt-BR': 'Premium',
        'ms-MY': 'Premium',
        es: 'Premium',
        fr: 'Premium',
        ja: 'プレミアム',
        id: 'Premium',
      },
      features: {
        en: [
          { feature: 'Characters Per Paste', free: '500', premium: 'Unlimited' },
          { feature: 'Daily Usage', free: 'Unlimited', premium: 'Unlimited' },
          { feature: 'Account Required', free: 'No', premium: 'Yes' },
          { feature: 'Document Cleaning', free: '—', premium: 'Word & Pages' },
          { feature: 'Detection Engine', free: 'Same', premium: 'Same' },
          { feature: 'Works on Text From Any Source', free: '✓', premium: '✓' },
          { feature: 'Browser Privacy', free: '✓', premium: '✓' },
        ],
        de: [
          { feature: 'Zeichen pro Einfügen', free: '500', premium: 'Unbegrenzt' },
          { feature: 'Tägliche Nutzung', free: 'Unbegrenzt', premium: 'Unbegrenzt' },
          { feature: 'Konto erforderlich', free: 'Nein', premium: 'Ja' },
          { feature: 'Dokumentenbereinigung', free: '—', premium: 'Word & Pages' },
          { feature: 'Erkennungs-Engine', free: 'Gleich', premium: 'Gleich' },
          { feature: 'Funktioniert mit Text aus jeder Quelle', free: '✓', premium: '✓' },
          { feature: 'Browser-Privatsphäre', free: '✓', premium: '✓' },
        ],
        'pt-BR': [
          { feature: 'Caracteres Por Colagem', free: '500', premium: 'Ilimitado' },
          { feature: 'Uso Diário', free: 'Ilimitado', premium: 'Ilimitado' },
          { feature: 'Conta Necessária', free: 'Não', premium: 'Sim' },
          { feature: 'Limpeza de Documentos', free: '—', premium: 'Word & Pages' },
          { feature: 'Mecanismo de Detecção', free: 'Igual', premium: 'Igual' },
          { feature: 'Funciona com Texto de Qualquer Origem', free: '✓', premium: '✓' },
          { feature: 'Privacidade do Navegador', free: '✓', premium: '✓' },
        ],
        'ms-MY': [
          { feature: 'Aksara Setiap Tampal', free: '500', premium: 'Tanpa had' },
          { feature: 'Penggunaan Harian', free: 'Tanpa had', premium: 'Tanpa had' },
          { feature: 'Akaun Diperlukan', free: 'Tidak', premium: 'Ya' },
          { feature: 'Pembersihan Dokumen', free: '—', premium: 'Word & Pages' },
          { feature: 'Enjin Pengesanan', free: 'Sama', premium: 'Sama' },
          { feature: 'Berfungsi dengan Teks dari Mana-Mana Sumber', free: '✓', premium: '✓' },
          { feature: 'Privasi Pelayar', free: '✓', premium: '✓' },
        ],
        es: [
          { feature: 'Caracteres Por Pegado', free: '500', premium: 'Ilimitado' },
          { feature: 'Uso Diario', free: 'Ilimitado', premium: 'Ilimitado' },
          { feature: 'Cuenta Requerida', free: 'No', premium: 'Sí' },
          { feature: 'Limpieza de Documentos', free: '—', premium: 'Word & Pages' },
          { feature: 'Motor de Detección', free: 'Igual', premium: 'Igual' },
          { feature: 'Funciona con Texto de Cualquier Origen', free: '✓', premium: '✓' },
          { feature: 'Privacidad del Navegador', free: '✓', premium: '✓' },
        ],
        fr: [
          { feature: 'Caractères Par Collage', free: '500', premium: 'Illimité' },
          { feature: 'Utilisation Quotidienne', free: 'Illimitée', premium: 'Illimitée' },
          { feature: 'Compte Requis', free: 'Non', premium: 'Oui' },
          { feature: 'Nettoyage de Documents', free: '—', premium: 'Word & Pages' },
          { feature: 'Moteur de Détection', free: 'Identique', premium: 'Identique' },
          { feature: 'Fonctionne avec du Texte de Toute Origine', free: '✓', premium: '✓' },
          { feature: 'Confidentialité Navigateur', free: '✓', premium: '✓' },
        ],
        ja: [
          { feature: '貼り付けあたりの文字数', free: '500', premium: '無制限' },
          { feature: '1日の使用回数', free: '無制限', premium: '無制限' },
          { feature: 'アカウント必要', free: 'いいえ', premium: 'はい' },
          { feature: 'ドキュメントクリーニング', free: '—', premium: 'Word & Pages' },
          { feature: '検出エンジン', free: '同一', premium: '同一' },
          { feature: '任意のソースのテキストに対応', free: '✓', premium: '✓' },
          { feature: 'ブラウザプライバシー', free: '✓', premium: '✓' },
        ],
        id: [
          { feature: 'Karakter Per Tempel', free: '500', premium: 'Tak Terbatas' },
          { feature: 'Penggunaan Harian', free: 'Tak Terbatas', premium: 'Tak Terbatas' },
          { feature: 'Akun Diperlukan', free: 'Tidak', premium: 'Ya' },
          { feature: 'Pembersihan Dokumen', free: '—', premium: 'Word & Pages' },
          { feature: 'Mesin Deteksi', free: 'Sama', premium: 'Sama' },
          { feature: 'Bekerja dengan Teks dari Sumber Mana Pun', free: '✓', premium: '✓' },
          { feature: 'Privasi Browser', free: '✓', premium: '✓' },
        ],
      },
    },
    useCases: {
      title: {
        en: 'Who Benefits from Clean AI Text?',
        de: 'Wer profitiert von sauberem KI-Text?',
        'pt-BR': 'Quem Se Beneficia de Texto de IA Limpo?',
        'ms-MY': 'Siapa yang Mendapat Manfaat daripada Teks AI Bersih?',
        es: '¿Quién Se Beneficia del Texto de IA Limpio?',
        fr: 'Qui Bénéficie d\'un Texte IA Propre ?',
        ja: 'クリーンなAIテキストの恩恵を受けるのは誰？',
        id: 'Siapa yang Diuntungkan dari Teks AI Bersih?',
      },
      items: {
        en: [
          { icon: GraduationCap, title: 'Students & Academics', description: 'Clean AI-assisted research notes, summaries, and drafts so hidden characters don\'t corrupt citations, break formatting, or skew word and character counts. Follow your institution\'s AI citation policies.' },
          { icon: FileText, title: 'Content Creators', description: 'Bloggers, writers, and marketers can ensure AI-assisted content is free from hidden watermarks before publishing to any platform.' },
          { icon: Users, title: 'Business Professionals', description: 'Clean emails, reports, and presentations drafted with AI assistance. Maintain professional document standards.' },
          { icon: Type, title: 'Developers', description: 'Remove watermarks from AI-generated documentation, code comments, and README files to prevent potential parsing issues.' },
        ],
        de: [
          { icon: GraduationCap, title: 'Studierende & Akademiker', description: 'Bereinigen Sie KI-unterstützte Recherchenotizen, Zusammenfassungen und Entwürfe, damit versteckte Zeichen keine Zitate beschädigen, keine Formatierung stören und keine Wort- und Zeichenzählungen verfälschen. Befolgen Sie die KI-Zitationsrichtlinien Ihrer Institution.' },
          { icon: FileText, title: 'Content-Ersteller', description: 'Blogger und Autoren können sicherstellen, dass KI-unterstützte Inhalte frei von versteckten Wasserzeichen sind.' },
          { icon: Users, title: 'Geschäftsleute', description: 'Bereinigen Sie E-Mails, Berichte und Präsentationen, die mit KI-Unterstützung erstellt wurden.' },
          { icon: Type, title: 'Entwickler', description: 'Entfernen Sie Wasserzeichen aus KI-generierter Dokumentation und Code-Kommentaren.' },
        ],
        'pt-BR': [
          { icon: GraduationCap, title: 'Estudantes & Acadêmicos', description: 'Limpe notas de pesquisa, resumos e rascunhos assistidos por IA para que caracteres ocultos não corrompam citações, quebrem a formatação ou distorçam a contagem de palavras e caracteres. Siga as políticas de citação de IA da sua instituição.' },
          { icon: FileText, title: 'Criadores de Conteúdo', description: 'Blogueiros e escritores podem garantir que o conteúdo assistido por IA esteja livre de marcas d\'água ocultas.' },
          { icon: Users, title: 'Profissionais de Negócios', description: 'Limpe emails, relatórios e apresentações redigidos com assistência de IA.' },
          { icon: Type, title: 'Desenvolvedores', description: 'Remova marcas d\'água de documentação gerada por IA e comentários de código.' },
        ],
        'ms-MY': [
          { icon: GraduationCap, title: 'Pelajar & Akademik', description: 'Bersihkan nota penyelidikan, ringkasan dan draf yang dibantu AI supaya aksara tersembunyi tidak merosakkan petikan, mengganggu format, atau mengubah kiraan perkataan dan aksara. Ikut polisi petikan AI institusi anda.' },
          { icon: FileText, title: 'Pencipta Kandungan', description: 'Blogger dan penulis boleh memastikan kandungan yang dibantu AI bebas daripada tanda air tersembunyi.' },
          { icon: Users, title: 'Profesional Perniagaan', description: 'Bersihkan email, laporan dan pembentangan yang didraf dengan bantuan AI.' },
          { icon: Type, title: 'Pembangun', description: 'Buang tanda air daripada dokumentasi yang dijana AI dan komen kod.' },
        ],
        es: [
          { icon: GraduationCap, title: 'Estudiantes & Académicos', description: 'Limpia notas de investigación, resúmenes y borradores asistidos por IA para que los caracteres ocultos no corrompan citas, rompan el formato o distorsionen el recuento de palabras y caracteres. Sigue las políticas de citación de IA de tu institución.' },
          { icon: FileText, title: 'Creadores de Contenido', description: 'Bloggers y escritores pueden asegurar que el contenido asistido por IA esté libre de marcas de agua ocultas.' },
          { icon: Users, title: 'Profesionales de Negocios', description: 'Limpia emails, informes y presentaciones redactados con asistencia de IA.' },
          { icon: Type, title: 'Desarrolladores', description: 'Elimina marcas de agua de documentación generada por IA y comentarios de código.' },
        ],
        fr: [
          { icon: GraduationCap, title: 'Étudiants et Universitaires', description: 'Nettoyez vos notes de recherche, résumés et brouillons assistés par IA afin que les caractères cachés ne corrompent pas vos citations, ne cassent pas la mise en forme et ne faussent pas vos décomptes de mots et de caractères. Suivez les politiques de citation IA de votre établissement.' },
          { icon: FileText, title: 'Créateurs de Contenu', description: 'Blogueurs et rédacteurs peuvent s\'assurer que le contenu assisté par IA est exempt de filigranes cachés avant publication.' },
          { icon: Users, title: 'Professionnels', description: 'Nettoyez les emails, rapports et présentations rédigés avec assistance IA. Maintenez des standards professionnels.' },
          { icon: Type, title: 'Développeurs', description: 'Supprimez les filigranes de la documentation générée par IA et des commentaires de code pour éviter les problèmes d\'analyse.' },
        ],
        ja: [
          { icon: GraduationCap, title: '学生・研究者', description: 'AI支援の研究ノート、要約、下書きをクリーニングし、隠れた文字が引用を壊したり、書式を崩したり、単語数・文字数を狂わせたりするのを防ぐ。所属機関のAI引用ポリシーに従うこと。' },
          { icon: FileText, title: 'コンテンツクリエイター', description: 'ブロガーやライターはAI支援コンテンツを公開前に隠し透かしのないものにできます。' },
          { icon: Users, title: 'ビジネスプロフェッショナル', description: 'AI支援で作成したメール、レポート、プレゼンテーションをクリーニング。プロフェッショナルな文書基準を維持。' },
          { icon: Type, title: '開発者', description: 'AI生成のドキュメント、コードコメント、READMEファイルから透かしを除去し、パース問題を防止。' },
        ],
        id: [
          { icon: GraduationCap, title: 'Mahasiswa & Akademisi', description: 'Bersihkan catatan penelitian, ringkasan, dan draft yang dibantu AI agar karakter tersembunyi tidak merusak kutipan, mengganggu format, atau mengacaukan penghitungan kata dan karakter. Ikuti kebijakan kutipan AI institusi Anda.' },
          { icon: FileText, title: 'Kreator Konten', description: 'Blogger dan penulis dapat memastikan konten yang dibantu AI bebas dari watermark tersembunyi sebelum publikasi.' },
          { icon: Users, title: 'Profesional Bisnis', description: 'Bersihkan email, laporan, dan presentasi yang dibuat dengan bantuan AI. Pertahankan standar dokumen profesional.' },
          { icon: Type, title: 'Developer', description: 'Hapus watermark dari dokumentasi yang dihasilkan AI, komentar kode, dan file README untuk mencegah masalah parsing.' },
        ],
      },
    },
    ethicalNote: {
      title: {
        en: 'A Note on Ethical Use',
        de: 'Hinweis zur ethischen Nutzung',
        'pt-BR': 'Uma Nota Sobre Uso Ético',
        'ms-MY': 'Nota Mengenai Penggunaan Beretika',
        es: 'Una Nota Sobre el Uso Ético',
        fr: 'Note sur l\'Utilisation Éthique',
        ja: '倫理的な使用について',
        id: 'Catatan tentang Penggunaan Etis',
      },
      content: {
        en: 'This tool removes technical watermark characters from text. It does not alter, rewrite, or disguise AI-generated content. We encourage ethical use: always follow your institution\'s or organization\'s AI usage policies, and properly cite AI-assisted work where required. Our tool is designed to give you clean text, not to bypass academic integrity standards.',
        de: 'Dieses Tool entfernt technische Wasserzeichen-Zeichen aus Text. Es verändert, schreibt nicht um oder tarnt KI-generierte Inhalte nicht. Wir ermutigen zur ethischen Nutzung: Befolgen Sie immer die KI-Nutzungsrichtlinien Ihrer Institution und zitieren Sie KI-unterstützte Arbeit ordnungsgemäß.',
        'pt-BR': 'Esta ferramenta remove caracteres de marca d\'água técnicos do texto. Ela não altera, reescreve ou disfarça conteúdo gerado por IA. Encorajamos o uso ético: sempre siga as políticas de uso de IA da sua instituição e cite adequadamente o trabalho assistido por IA quando necessário.',
        'ms-MY': 'Alat ini membuang aksara tanda air teknikal daripada teks. Ia tidak mengubah, menulis semula atau menyamarkan kandungan yang dijana AI. Kami menggalakkan penggunaan beretika: sentiasa ikut polisi penggunaan AI institusi anda dan petik kerja yang dibantu AI dengan betul.',
        es: 'Esta herramienta elimina caracteres de marca de agua técnicos del texto. No altera, reescribe ni disfraza contenido generado por IA. Fomentamos el uso ético: siempre sigue las políticas de uso de IA de tu institución y cita adecuadamente el trabajo asistido por IA cuando sea necesario.',
        fr: 'Cet outil supprime les caractères de filigrane techniques du texte. Il ne modifie, réécrit ni ne déguise le contenu généré par IA. Nous encourageons une utilisation éthique : respectez toujours les politiques d\'utilisation de l\'IA de votre établissement et citez correctement le travail assisté par IA lorsque requis.',
        ja: 'このツールはテキストから技術的な透かし文字を除去します。AI生成コンテンツを変更、書き換え、偽装することはありません。倫理的な使用を推奨します：常に所属機関のAI使用ポリシーに従い、必要な場合はAI支援作業を適切に引用してください。',
        id: 'Alat ini menghapus karakter watermark teknis dari teks. Ini tidak mengubah, menulis ulang, atau menyamarkan konten yang dihasilkan AI. Kami mendorong penggunaan etis: selalu ikuti kebijakan penggunaan AI institusi Anda dan kutip dengan benar pekerjaan yang dibantu AI bila diperlukan.',
      },
    },
  };

  const faqs = {
    en: [
      {
        question: 'Does this work with text from all AI models?',
        answer: 'Yes, it works on text from any source. It finds and removes invisible Unicode characters. It does not affect watermarks embedded in a model\'s word choices, such as Google\'s SynthID, or Anthropic\'s embedded watermark in models launched on or after 2 August 2026.',
        icon: Bot,
      },
      {
        question: 'Is it really free?',
        answer: 'Yes, 500 characters per paste, unlimited uses, forever free. No credit card, no signup needed.',
        icon: Gift,
      },
      {
        question: 'Is my text private?',
        answer: 'Completely. All processing happens in your browser. We never see, store, or transmit your text.',
        icon: Shield,
      },
      {
        question: 'Will it change my text formatting?',
        answer: 'No. Only invisible watermark characters are removed. Your visible text stays exactly the same.',
        icon: FileText,
      },
      {
        question: 'Does removing invisible characters affect AI-writing detection?',
        answer: 'No. Detectors like Turnitin and GPTZero analyse your visible writing, patterns in word choice and sentence rhythm, not invisible Unicode characters. Cleaning those characters fixes formatting, code and document issues; it does not change an AI-detection result. Always use AI-assisted content honestly and cite it properly.',
        icon: GraduationCap,
      },
      {
        question: 'What\'s the difference with premium?',
        answer: 'Free: 500 chars/paste. Premium: Unlimited characters + document cleaning (Word, Pages files).',
        icon: Zap,
      },
    ],
    de: [
      {
        question: 'Funktioniert es mit Text von allen KI-Modellen?',
        answer: 'Ja, es funktioniert mit Text aus jeder Quelle. Es findet und entfernt unsichtbare Unicode-Zeichen. Es hat keinen Einfluss auf Wasserzeichen, die in die Wortwahl eines Modells eingebettet sind, wie Googles SynthID oder Anthropics eingebettetes Wasserzeichen in Modellen, die ab dem 2. August 2026 eingeführt werden.',
        icon: Bot,
      },
      {
        question: 'Ist es wirklich kostenlos?',
        answer: 'Ja, 500 Zeichen pro Einfügen, unbegrenzte Nutzung, für immer kostenlos. Keine Kreditkarte, keine Anmeldung.',
        icon: Gift,
      },
      {
        question: 'Ist mein Text privat?',
        answer: 'Vollständig. Alle Verarbeitung erfolgt in Ihrem Browser. Wir sehen, speichern oder übertragen Ihren Text nie.',
        icon: Shield,
      },
      {
        question: 'Wird meine Textformatierung geändert?',
        answer: 'Nein. Nur unsichtbare Wasserzeichen werden entfernt. Ihr sichtbarer Text bleibt genau gleich.',
        icon: FileText,
      },
      {
        question: 'Beeinflusst das Entfernen unsichtbarer Zeichen die KI-Texterkennung?',
        answer: 'Nein. Detektoren wie Turnitin und GPTZero analysieren Ihren sichtbaren Text, Muster in Wortwahl und Satzrhythmus, nicht unsichtbare Unicode-Zeichen. Das Bereinigen dieser Zeichen behebt Formatierungs-, Code- und Dokumentprobleme; es ändert kein KI-Erkennungsergebnis. Nutzen Sie KI-Inhalte stets ehrlich und zitieren Sie ordnungsgemäß.',
        icon: GraduationCap,
      },
      {
        question: 'Was ist der Unterschied zu Premium?',
        answer: 'Kostenlos: 500 Zeichen/Einfügen. Premium: Unbegrenzte Zeichen + Dokumentenreinigung.',
        icon: Zap,
      },
    ],
    'pt-BR': [
      {
        question: 'Funciona com texto de todos os modelos de IA?',
        answer: 'Sim, funciona com texto de qualquer origem. Ele encontra e remove caracteres Unicode invisíveis. Não afeta marcas d\'água incorporadas nas escolhas de palavras de um modelo, como o SynthID do Google, ou a marca d\'água incorporada da Anthropic em modelos lançados a partir de 2 de agosto de 2026.',
        icon: Bot,
      },
      {
        question: 'É realmente grátis?',
        answer: 'Sim, 500 caracteres por colagem, usos ilimitados, grátis para sempre. Sem cartão de crédito, sem cadastro.',
        icon: Gift,
      },
      {
        question: 'Meu texto é privado?',
        answer: 'Completamente. Todo processamento acontece no seu navegador. Nunca vemos, armazenamos ou transmitimos seu texto.',
        icon: Shield,
      },
      {
        question: 'Vai mudar a formatação do meu texto?',
        answer: 'Não. Apenas caracteres de marca d\'água invisíveis são removidos. Seu texto visível permanece exatamente igual.',
        icon: FileText,
      },
      {
        question: 'Remover caracteres invisíveis afeta a detecção de escrita por IA?',
        answer: 'Não. Detectores como Turnitin e GPTZero analisam seu texto visível, padrões na escolha de palavras e no ritmo das frases, não caracteres Unicode invisíveis. Limpar esses caracteres resolve problemas de formatação, código e documentos; não altera o resultado de detecção de IA. Use sempre conteúdo assistido por IA com honestidade e cite corretamente.',
        icon: GraduationCap,
      },
      {
        question: 'Qual a diferença com premium?',
        answer: 'Grátis: 500 chars/colagem. Premium: Caracteres ilimitados + limpeza de documentos.',
        icon: Zap,
      },
    ],
    'ms-MY': [
      {
        question: 'Adakah ini berfungsi dengan teks dari semua model AI?',
        answer: 'Ya, ia berfungsi dengan teks dari mana-mana sumber. Ia mencari dan membuang aksara Unicode tidak kelihatan. Ia tidak menjejaskan tanda air yang terbenam dalam pemilihan perkataan model, seperti SynthID Google, atau tanda air terbenam Anthropic dalam model yang dilancarkan pada atau selepas 2 Ogos 2026.',
        icon: Bot,
      },
      {
        question: 'Adakah ia benar-benar percuma?',
        answer: 'Ya, 500 aksara setiap tampal, penggunaan tanpa had, percuma selamanya. Tiada kad kredit, tiada pendaftaran.',
        icon: Gift,
      },
      {
        question: 'Adakah teks saya peribadi?',
        answer: 'Sepenuhnya. Semua pemprosesan berlaku dalam pelayar anda. Kami tidak pernah melihat, menyimpan atau menghantar teks anda.',
        icon: Shield,
      },
      {
        question: 'Adakah ia akan menukar pemformatan teks saya?',
        answer: 'Tidak. Hanya aksara tanda air tidak kelihatan dibuang. Teks anda yang kelihatan kekal sama.',
        icon: FileText,
      },
      {
        question: 'Adakah membuang aksara tidak kelihatan menjejaskan pengesanan penulisan AI?',
        answer: 'Tidak. Pengesan seperti Turnitin dan GPTZero menganalisis teks anda yang kelihatan, corak dalam pemilihan perkataan dan rentak ayat, bukan aksara Unicode tidak kelihatan. Membersihkan aksara tersebut menyelesaikan masalah format, kod dan dokumen; ia tidak mengubah keputusan pengesanan AI. Sentiasa gunakan kandungan yang dibantu AI secara jujur dan petik dengan betul.',
        icon: GraduationCap,
      },
      {
        question: 'Apakah perbezaan dengan premium?',
        answer: 'Percuma: 500 aksara/tampal. Premium: Aksara tanpa had + pembersihan dokumen.',
        icon: Zap,
      },
    ],
    es: [
      {
        question: '¿Funciona con texto de todos los modelos de IA?',
        answer: 'Sí, funciona con texto de cualquier origen. Encuentra y elimina caracteres Unicode invisibles. No afecta a las marcas de agua incrustadas en la elección de palabras de un modelo, como SynthID de Google, ni a la marca de agua incrustada de Anthropic en modelos lanzados a partir del 2 de agosto de 2026.',
        icon: Bot,
      },
      {
        question: '¿Es realmente gratis?',
        answer: 'Sí, 500 caracteres por pegado, usos ilimitados, gratis para siempre. Sin tarjeta de crédito, sin registro.',
        icon: Gift,
      },
      {
        question: '¿Mi texto es privado?',
        answer: 'Completamente. Todo el procesamiento ocurre en tu navegador. Nunca vemos, almacenamos ni transmitimos tu texto.',
        icon: Shield,
      },
      {
        question: '¿Cambiará el formato de mi texto?',
        answer: 'No. Solo se eliminan los caracteres de marca de agua invisibles. Tu texto visible permanece exactamente igual.',
        icon: FileText,
      },
      {
        question: '¿Eliminar caracteres invisibles afecta la detección de escritura por IA?',
        answer: 'No. Detectores como Turnitin y GPTZero analizan tu texto visible, patrones en la elección de palabras y el ritmo de las frases, no caracteres Unicode invisibles. Limpiar esos caracteres resuelve problemas de formato, código y documentos; no cambia el resultado de detección de IA. Usa siempre el contenido asistido por IA con honestidad y cítalo correctamente.',
        icon: GraduationCap,
      },
      {
        question: '¿Cuál es la diferencia con premium?',
        answer: 'Gratis: 500 chars/pegado. Premium: Caracteres ilimitados + limpieza de documentos.',
        icon: Zap,
      },
    ],
    fr: [
      {
        question: 'Cela fonctionne-t-il avec tous les modèles d\'IA ?',
        answer: 'Oui, il fonctionne sur du texte de toute origine. Il repère et supprime les caractères Unicode invisibles. Il n\'a aucun effet sur les filigranes intégrés dans le choix des mots d\'un modèle, comme SynthID de Google, ou le filigrane intégré d\'Anthropic dans les modèles lancés à partir du 2 août 2026.',
        icon: Bot,
      },
      {
        question: 'Est-ce vraiment gratuit ?',
        answer: 'Oui, 500 caractères par collage, utilisations illimitées, gratuit pour toujours. Sans carte bancaire, sans inscription.',
        icon: Gift,
      },
      {
        question: 'Mon texte reste-t-il privé ?',
        answer: 'Totalement. Tout le traitement se fait dans votre navigateur. Nous ne voyons, stockons ni transmettons jamais votre texte.',
        icon: Shield,
      },
      {
        question: 'Cela va-t-il modifier la mise en forme de mon texte ?',
        answer: 'Non. Seuls les caractères de filigrane invisibles sont supprimés. Votre texte visible reste exactement identique.',
        icon: FileText,
      },
      {
        question: 'La suppression des caractères invisibles affecte-t-elle la détection d\'écriture IA ?',
        answer: 'Non. Les détecteurs comme Turnitin et GPTZero analysent votre texte visible, les schémas du choix des mots et du rythme des phrases, et non les caractères Unicode invisibles. Nettoyer ces caractères corrige les problèmes de mise en forme, de code et de documents ; cela ne change pas un résultat de détection IA. Utilisez toujours le contenu assisté par IA avec honnêteté et citez correctement vos sources.',
        icon: GraduationCap,
      },
      {
        question: 'Quelle est la différence avec Premium ?',
        answer: 'Gratuit : 500 caractères/collage. Premium : Caractères illimités + nettoyage de documents (Word, Pages).',
        icon: Zap,
      },
    ],
    ja: [
      {
        question: 'すべてのAIモデルのテキストに対応していますか？',
        answer: 'はい、あらゆる出所のテキストに対応しています。不可視のUnicode文字を見つけて除去します。GoogleのSynthIDや、2026年8月2日以降にリリースされたAnthropicのモデルに埋め込まれた透かしなど、モデルの単語選択に埋め込まれた透かしには影響しません。',
        icon: Bot,
      },
      {
        question: '本当に無料ですか？',
        answer: 'はい、1回の貼り付けにつき500文字まで無料、使用回数無制限、永久無料です。クレジットカードも登録も不要です。',
        icon: Gift,
      },
      {
        question: 'テキストのプライバシーは保護されますか？',
        answer: '完全に保護されます。すべての処理はブラウザ内で行われます。テキストを閲覧、保存、送信することはありません。',
        icon: Shield,
      },
      {
        question: 'テキストの書式は変更されますか？',
        answer: 'いいえ。見えない透かし文字のみが除去されます。表示されるテキストは完全にそのまま維持されます。',
        icon: FileText,
      },
      {
        question: '不可視文字の除去はAI文章検出に影響しますか？',
        answer: 'いいえ。TurnitinやGPTZeroなどの検出ツールは、不可視のUnicode文字ではなく、表示される文章、単語選択や文のリズムのパターンを分析します。これらの文字を除去すると書式・コード・ドキュメントの問題は解消されますが、AI検出結果は変わりません。AI支援コンテンツは常に誠実に使用し、適切に引用してください。',
        icon: GraduationCap,
      },
      {
        question: 'プレミアムとの違いは何ですか？',
        answer: '無料：500文字/貼り付け。プレミアム：無制限の文字数 + ドキュメントクリーニング（Word、Pagesファイル）。',
        icon: Zap,
      },
    ],
    id: [
      {
        question: 'Apakah ini bekerja dengan teks dari semua model AI?',
        answer: 'Ya, alat ini bekerja dengan teks dari sumber mana pun. Ia menemukan dan menghapus karakter Unicode tak terlihat. Ia tidak berpengaruh pada watermark yang tertanam dalam pilihan kata model, seperti SynthID milik Google, atau watermark tertanam Anthropic pada model yang diluncurkan pada atau setelah 2 Agustus 2026.',
        icon: Bot,
      },
      {
        question: 'Apakah benar-benar gratis?',
        answer: 'Ya, 500 karakter per tempel, penggunaan tak terbatas, gratis selamanya. Tanpa kartu kredit, tanpa pendaftaran.',
        icon: Gift,
      },
      {
        question: 'Apakah teks saya tetap privat?',
        answer: 'Sepenuhnya. Semua pemrosesan terjadi di browser Anda. Kami tidak pernah melihat, menyimpan, atau mengirimkan teks Anda.',
        icon: Shield,
      },
      {
        question: 'Apakah ini akan mengubah format teks saya?',
        answer: 'Tidak. Hanya karakter watermark tak terlihat yang dihapus. Teks yang terlihat tetap sama persis.',
        icon: FileText,
      },
      {
        question: 'Apakah menghapus karakter tak terlihat memengaruhi deteksi tulisan AI?',
        answer: 'Tidak. Detektor seperti Turnitin dan GPTZero menganalisis teks Anda yang terlihat, pola dalam pemilihan kata dan ritme kalimat, bukan karakter Unicode tak terlihat. Membersihkan karakter tersebut memperbaiki masalah format, kode, dan dokumen; tidak mengubah hasil deteksi AI. Selalu gunakan konten yang dibantu AI secara jujur dan kutip dengan benar.',
        icon: GraduationCap,
      },
      {
        question: 'Apa perbedaan dengan premium?',
        answer: 'Gratis: 500 karakter/tempel. Premium: Karakter tak terbatas + pembersihan dokumen (file Word, Pages).',
        icon: Zap,
      },
    ],
  };

  const currentFAQs = faqs[locale as LocaleKey] || faqs.en;
  const currentLocale = locale as LocaleKey;

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const faqSchema = generateFAQSchema(currentFAQs);
      const cleanup = injectFAQSchema(faqSchema);
      return cleanup;
    } catch (error) {
      console.error('[FreeAIText FAQ] Failed to generate or inject FAQ schema:', error);
    }
  }, [locale, currentFAQs]);

  return (
    <div className="min-h-screen">
      {/* Compact Hero - Aggressive Intent Match */}
      <section className="py-8 px-4">
        <div className="max-w-[1400px] mx-auto text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 dark:text-white mb-3">
            {t.hero.title[currentLocale]}
          </h1>

          <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-4">
            {t.hero.subtitle[currentLocale]}
          </p>

          <a
            href="#remover"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all active:scale-[0.98]"
          >
            {t.hero.cta[currentLocale]}
          </a>
        </div>
      </section>

      {/* Tool Section - Very High on Page */}
      <section id="remover" className="px-4">
        <div className="max-w-[1400px] mx-auto mb-4">
          <p className="text-center text-neutral-500 dark:text-neutral-400 text-sm font-medium">
            {t.toolInstruction[currentLocale]}
          </p>
        </div>
        <div className="max-w-[1400px] mx-auto">
          <WatermarkRemoverClient user={user} />
        </div>
      </section>

      {/* Free Tier Highlights - Compact Bullets */}
      <section className="py-10 px-4">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-xl font-bold text-neutral-900 dark:text-white text-center mb-6 flex items-center justify-center gap-2">
            <Gift size={20} className="text-blue-600 dark:text-blue-400" />
            {t.freeTierHighlights.title[currentLocale]}
          </h2>

          <ul className="space-y-3">
            {t.freeTierHighlights.items[currentLocale].map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle2 size={18} className="text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <span className="text-neutral-700 dark:text-neutral-300">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Proof Section - Small & Compact */}
      <section className="py-8 px-4 border-y border-neutral-200 dark:border-neutral-800">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex justify-center items-center gap-8 md:gap-12">
            {t.proofSection.stats[currentLocale].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-blue-600 dark:text-blue-400">{stat.value}</div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Short FAQ Section */}
      <section className="py-10 px-4">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-xl font-bold text-neutral-900 dark:text-white text-center mb-6 flex items-center justify-center gap-2">
            <HelpCircle size={20} className="text-blue-600 dark:text-blue-400" />
            {t.faqTitle[currentLocale]}
          </h2>

          <div className="space-y-2">
            {currentFAQs.map((faq, index) => {
              const Icon = faq.icon;
              const isOpen = openFAQIndex === index;

              return (
                <div
                  key={index}
                  className="border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden bg-white dark:bg-neutral-900"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full flex items-center justify-between p-4 text-left"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <Icon size={18} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
                      <span className="font-medium text-neutral-900 dark:text-white text-sm">
                        {faq.question}
                      </span>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>

                  <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-40 pb-4' : 'max-h-0'}`}>
                    <p className="px-4 pl-11 text-sm text-neutral-600 dark:text-neutral-400">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Step-by-Step Guide Section */}
      <section className="py-12 px-4 bg-white dark:bg-neutral-900 border-y border-neutral-200 dark:border-neutral-800">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white text-center mb-10">
            {t.stepByStep.title[currentLocale]}
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {t.stepByStep.steps[currentLocale].map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-neutral-50 dark:bg-neutral-800 rounded-xl p-6 border border-neutral-200 dark:border-neutral-700 h-full">
                  <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight className="text-blue-400 dark:text-blue-500" size={24} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Invisible Characters Table Section */}
      <section className="py-12 px-4">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white text-center mb-4">
            {t.invisibleChars.title[currentLocale]}
          </h2>
          <p className="text-center text-neutral-600 dark:text-neutral-400 mb-8 max-w-2xl mx-auto">
            {t.invisibleChars.description[currentLocale]}
          </p>

          <div className="overflow-x-auto rounded-xl border border-neutral-200 dark:border-neutral-700">
            <table className="w-full border-collapse bg-white dark:bg-neutral-800">
              <thead>
                <tr className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
                  <th className="p-4 text-left font-semibold">{t.invisibleChars.tableHeaders[currentLocale].code}</th>
                  <th className="p-4 text-left font-semibold">{t.invisibleChars.tableHeaders[currentLocale].name}</th>
                  <th className="p-4 text-left font-semibold">{t.invisibleChars.tableHeaders[currentLocale].purpose}</th>
                </tr>
              </thead>
              <tbody>
                {t.invisibleChars.chars[currentLocale].map((char, index) => (
                  <tr key={index} className="border-b border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                    <td className="p-4 font-mono text-blue-600 dark:text-blue-400 text-sm">{char.code}</td>
                    <td className="p-4 font-medium text-neutral-900 dark:text-white">{char.name}</td>
                    <td className="p-4 text-neutral-600 dark:text-neutral-400 text-sm">{char.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Common Problems Section */}
      <section className="py-12 px-4 bg-gradient-to-b from-red-50/50 to-transparent dark:from-red-900/10 dark:to-transparent">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white text-center mb-10">
            {t.textProblems.title[currentLocale]}
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {t.textProblems.problems[currentLocale].map((problem, index) => {
              const Icon = problem.icon;
              return (
                <div key={index} className="bg-white dark:bg-neutral-800 p-5 rounded-xl border border-neutral-200 dark:border-neutral-700">
                  <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center mb-3">
                    <Icon size={20} className="text-red-600 dark:text-red-400" />
                  </div>
                  <h3 className="font-semibold text-neutral-900 dark:text-white mb-2">{problem.title}</h3>
                  <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed">{problem.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Clean Section */}
      <section className="py-12 px-4">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white text-center mb-10">
            {t.whyClean.title[currentLocale]}
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {t.whyClean.reasons[currentLocale].map((reason, index) => {
              const Icon = reason.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 p-5 rounded-xl border border-blue-200 dark:border-blue-800">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mb-3">
                    <Icon size={20} className="text-white" />
                  </div>
                  <h3 className="font-semibold text-neutral-900 dark:text-white mb-2">{reason.title}</h3>
                  <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed">{reason.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-12 px-4 bg-white dark:bg-neutral-900 border-y border-neutral-200 dark:border-neutral-800">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white text-center mb-10">
            {t.useCases.title[currentLocale]}
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.useCases.items[currentLocale].map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Icon size={28} className="text-white" />
                  </div>
                  <h3 className="font-bold text-neutral-900 dark:text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-12 px-4">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white text-center mb-8">
            {t.comparison.title[currentLocale]}
          </h2>

          <div className="overflow-x-auto rounded-xl border border-neutral-200 dark:border-neutral-700 max-w-2xl mx-auto">
            <table className="w-full border-collapse bg-white dark:bg-neutral-800">
              <thead>
                <tr className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
                  <th className="p-4 text-left font-semibold">{t.comparison.featureLabel[currentLocale]}</th>
                  <th className="p-4 text-center font-semibold">{t.comparison.free[currentLocale]}</th>
                  <th className="p-4 text-center font-semibold">{t.comparison.premium[currentLocale]}</th>
                </tr>
              </thead>
              <tbody>
                {t.comparison.features[currentLocale].map((row, index) => (
                  <tr key={index} className="border-b border-neutral-200 dark:border-neutral-700">
                    <td className="p-4 font-medium text-neutral-900 dark:text-white">{row.feature}</td>
                    <td className="p-4 text-center text-neutral-600 dark:text-neutral-400">{row.free}</td>
                    <td className="p-4 text-center text-neutral-600 dark:text-neutral-400">{row.premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="text-center mt-6">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline font-medium"
            >
              {t.comparison.premiumCta[currentLocale]} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Ethical Note Section */}
      <section className="py-8 px-4">
        <div className="max-w-[1400px] mx-auto">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6">
            <h3 className="font-bold text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-2">
              <AlertTriangle size={18} />
              {t.ethicalNote.title[currentLocale]}
            </h3>
            <p className="text-sm text-amber-700 dark:text-amber-300 leading-relaxed">
              {t.ethicalNote.content[currentLocale]}
            </p>
          </div>
        </div>
      </section>

      {/* Related Tutorials - Internal Linking Block */}
      <section className="py-8 px-4">
        <div className="max-w-[1400px] mx-auto">
          <h3 className="text-lg font-bold text-neutral-900 dark:text-white text-center mb-4">
            {t.relatedTutorials.title[currentLocale]}
          </h3>
          <RelatedContentForPSEO
            pageId="free-ai-text-watermark"
            pageTitle={t.hero.title[currentLocale]}
            pageDescription={t.hero.subtitle[currentLocale]}
            locale={locale}
          />
        </div>
      </section>

      {/* Internal linking banner - Gemini Image Watermark Remover */}
      <GeminiImageBanner />

      {/* Related Tools */}
      <Suspense fallback={null}>
        <RelatedTools currentPage="free-ai-text" />
      </Suspense>

      <Suspense fallback={null}>
        <FeedbackCTA />
      </Suspense>
    </div>
  );
}
